import { configureStore, combineReducers } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import authReducer          from './slices/authSlice';
import blueCartReducer      from './slices/blueCartSlice';
import greenCartReducer     from './slices/greenCartSlice';
import cookieConsentReducer from './slices/cookieConsentSlice';

/**
 * Persistence strategy:
 *
 * Each slice has its OWN persist key. The Blue Cart and Green Cart MUST
 * never share a storage key (Design System §1.2 — "Don't break list").
 * Cookie consent is also persisted, separately, so the banner doesn't
 * re-prompt on every page load.
 *
 * Auth state is persisted but only the `user` and `token` survive — `status`
 * and `error` reset on rehydrate so the UI doesn't get stuck in a loading
 * or error state from a previous session.
 */

const authPersistConfig = {
  key: 'cr_auth',
  storage,
  whitelist: ['user', 'token'],
};

const blueCartPersistConfig = {
  key: 'cr_blue_cart', // separate key — never shared with green
  storage,
};

const greenCartPersistConfig = {
  key: 'cr_green_cart', // separate key — never shared with blue
  storage,
};

const cookieConsentPersistConfig = {
  key: 'cr_cookie_consent',
  storage,
};

const rootReducer = combineReducers({
  auth:          persistReducer(authPersistConfig, authReducer),
  blueCart:      persistReducer(blueCartPersistConfig, blueCartReducer),
  greenCart:     persistReducer(greenCartPersistConfig, greenCartReducer),
  cookieConsent: persistReducer(cookieConsentPersistConfig, cookieConsentReducer),
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // redux-persist dispatches non-serializable actions during rehydration
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

export const persistor = persistStore(store);

// Convenience export — kept un-typed (this codebase is JS, not TS).
export default store;
